import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PropTypes from 'prop-types';

// Flatten shipment for CSV row
const flattenShipment = (s) => {
  const booking = s.booking || {};
  const containers = s.containers || [];
  return {
    id: s.id,
    entryDate: s.entryDate || '',
    bookingNumber: booking.number || '',
    status: booking?.bookingStatus?.name || '',
    destination: booking?.destination?.name || '',
    shippingLine: booking?.shippingLine?.name || '',
    vessel: booking.vesselName || '',
    voyage: booking.voyageNumber || '',
    earliestReceivingDate: booking.earliestReceivingDate || '',
    latestReturnDate: booking.latestReturnDate || '',
    cutoffDate: booking.cutoffDate || '',
    estimatedSailingDate: booking.estimatedSailingDate || '',
    estimatedArrivalDate: booking.estimatedArrivalDate || '',
    containers: containers.length,
    vehicles: containers.reduce((sum,c)=> sum + ((c.shipmentItems?.length || 0) || (c.vehicles?.length || 0)),0),
    netWeight: containers.reduce((sum,c)=> sum + (c.netWeight || 0),0)
  };
};



// (previously there was a currency helper here; we now format using a provided currencySymbol per-export)
const vehicleDesc = (v)=>{

  const desc = [ v?.year, v?.make?.name || v?.make, v?.model?.name || v?.model].filter(Boolean).join(' ');
  return [desc.replace("‑",""), v?.arrivalNote].filter(Boolean).join('\n');

};
// Keep exportShipmentDetailPDF compatibility: delegate to exportShippingInstructionsPDF for now
export const exportShipmentDetailPDF = (shipment, filenamePrefix = 'shipment', options = {}, currencySymbol = '$', withConsigneeDock) => {

  const shipmentMode = shipment?.shipmentMode || shipment?.type;
  const isSplitShipment = ['LCL', 'SPL'].includes(shipmentMode);
  const documentId = shipment?.booking?.number || shipment?.id ||  'doc';
  const mode = options.mode || 'download';
  // when split shipment, we generate a shipping instructions PDF with one page per container/item, which is more suitable for previewing and printing; otherwise we generate a simpler single-page PDF focused on the overall shipment details
  if (isSplitShipment) {
    exportShippingInstructionsPDF(
      shipment,
      { ...options, mode, filename: `${filenamePrefix}_SplitShipment_${documentId}.pdf` },
      currencySymbol,
      withConsigneeDock,
      true
    );
  }

  return exportShippingInstructionsPDF(
    shipment,
    { ...options, mode, filename: `${filenamePrefix}_${documentId}.pdf` },
    currencySymbol,
    shipmentMode==="SPL" ? true : withConsigneeDock, // for SPL, we treat the consignee dock as the main consignee for display purposes
    false
  );
};
exportShipmentDetailPDF.prototype = {
  shipment: PropTypes.object.isRequired,
  filenamePrefix: PropTypes.string,
  options: PropTypes.object,
  currencySymbol: PropTypes.string,
  withConsigneeDock: PropTypes.bool,
}

export const exportShipmentsSummaryPDF = (shipments, filename='shipments_summary.pdf', options = {}) => {
  if(!shipments.length) return;
  const doc = new jsPDF('l','pt');
  const primary = [11,60,93];
  const rows = shipments.map(flattenShipment);
  const head = Object.keys(rows[0]);
  const totals = head.map(h => {
    if(['containers','vehicles','netWeight'].includes(h)){
      return rows.reduce((s,r)=> s + (Number(r[h])||0),0);
    }
    return '';
  });
  const headerClearance = 90;
  doc.autoTable({
    startY: headerClearance,
    head: [head],
    body: rows.map(r => head.map(h => r[h])).concat([totals]),
    theme: 'grid',
    headStyles: { fillColor: primary, textColor:[255,255,255] },
    styles: { fontSize:8, cellPadding:3 },
    alternateRowStyles: { fillColor: [245,248,252] },
    didDrawPage: (data) => {
      const pw = doc.internal.pageSize.getWidth();
      doc.setFillColor(...primary); doc.rect(0,0, pw, 70,'F');
      doc.setFontSize(13); doc.setFont(undefined,'normal'); doc.setTextColor(255,255,255); doc.text('Shipments Summary', 40, 60);
      doc.setFontSize(9); doc.setTextColor(230,240,247);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pw - 40, 24, { align:'right' });
      doc.text('Powered by Pinetechs.com', pw - 40, 42, { align:'right' });
      const footerY = doc.internal.pageSize.getHeight() - 48; // raised footer
      doc.setDrawColor(...primary); doc.setLineWidth(.5); doc.line(40, footerY - 12, pw - 40, footerY - 12);
      doc.setFontSize(8); doc.setTextColor(110,110,110);
      doc.text('VMS PRO • Powered by Pinetechs.com', 40, footerY);
      doc.text(`Page ${data.pageNumber}`, pw - 40, footerY, { align:'right' });
    },
    didParseCell: (data) => {
      if(data.row.index === rows.length){ // totals row
        data.cell.styles.fontStyle = 'bold';
        if(data.column.index === 0) data.cell.text = ['Totals'];
      }
    }
  });
  const { mode = 'download' } = options; // 'preview' | 'download'
  if(mode === 'preview') {
    try {
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');
    } catch(e){
      doc.output('dataurlnewwindow');
    }
  } else {
    doc.save(filename);
  }
};

const getJobVehiclesCount = (job) =>
  (job?.containers || []).reduce((sum, container) => sum + (container?.shipmentItems?.length || 0), 0);

const getJobContainersCount = (job) => (job?.containers || []).length;

const getJobHasHybridElectric = (job) =>
  (job?.containers || []).some((container) =>
    (container?.shipmentItems || []).some((item) => {
      const fuelType = item?.vehicle?.fuelType?.name || item?.vehicle?.fuelType;
      return ['hybrid', 'electric'].includes(fuelType?.toString().trim().toLowerCase());
    })
  );

const getValueName = (value) => value?.name || value || '';

const getJobVehicleTitle = (vehicle) =>
  [
    getValueName(vehicle?.vehicleType),
    vehicle?.year,
    getValueName(vehicle?.make),
    getValueName(vehicle?.model),
  ].filter(Boolean).join(' ') || 'Vehicle';

const getJobVehicleVin = (vehicle) =>
  vehicle?.vin || vehicle?.chassisNumber || vehicle?.chassisNo || vehicle?.serial || '-';

export const exportJobsResultsPDF = (jobs = [], filters = {}, options = {}) => {
  if (!jobs.length) return;

  const doc = new jsPDF('l', 'pt');
  const primary = [11, 60, 93];
  const filename = options.filename || 'jobs_results.pdf';
  const mode = options.mode || 'download';
  const includeSummary = options.includeSummary !== false;
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 28;
  const filterLines = [
    `Status: ${filters.Status || 'All'}`,
    `Date: ${filters.StartDate || '-'} to ${filters.EndDate || '-'}`,
    `Search: ${filters.MixSearch || '-'}`,
    `Sort: ${filters.SortBy || 'id'} ${filters.SortOrder || 'desc'}`,
    `Rows: ${jobs.length}`,
  ];

  const rows = jobs.map((job) => {
    const booking = job?.booking || {};
    const client = job?.client || {};
    return [
      job?.id || '-',
      job?.jobStatus || '-',
      job?.startDate || '-',
      booking?.number || '-',
      client?.name || '-',
      booking?.vesselName || '-',
      booking?.destination?.name || '-',
      booking?.earliestReceivingDate || '-',
      booking?.latestReturnDate || '-',
      getJobContainersCount(job),
      getJobVehiclesCount(job),
      getJobHasHybridElectric(job) ? 'Yes' : 'No',
    ];
  });

  if (includeSummary) {
    doc.autoTable({
      startY: 118,
      head: [[
        'ID',
        'Status',
        'Start',
        'Booking',
        'Client',
        'Vessel',
        'Destination',
        'ERD',
        'LRD',
        'Containers',
        'Vehicles',
        'Hybrid/Electric',
      ]],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: primary, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      margin: { left: marginX, right: marginX, top: 118, bottom: 40 },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 78 },
        2: { cellWidth: 58 },
        3: { cellWidth: 64 },
        4: { cellWidth: 82 },
        5: { cellWidth: 82 },
        6: { cellWidth: 78 },
        7: { cellWidth: 58 },
        8: { cellWidth: 58 },
        9: { halign: 'center', cellWidth: 50 },
        10: { halign: 'center', cellWidth: 46 },
        11: { halign: 'center', cellWidth: 60 },
      },
      didDrawPage: (data) => {
        doc.setFillColor(...primary);
        doc.rect(0, 0, pageWidth, 76, 'F');
        doc.setFontSize(15);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('Jobs Results', marginX, 32);
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(230, 240, 247);
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - marginX, 24, { align: 'right' });
        doc.text('VMS PRO - Powered by Pinetechs.com', pageWidth - marginX, 42, { align: 'right' });

        if (data.pageNumber === 1) {
          doc.setTextColor(70, 70, 70);
          doc.setFontSize(8);
          filterLines.forEach((line, index) => {
            doc.text(line, marginX + (index % 3) * 250, 94 + Math.floor(index / 3) * 12);
          });
        }

        const footerY = doc.internal.pageSize.getHeight() - 24;
        doc.setDrawColor(...primary);
        doc.setLineWidth(0.5);
        doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);
        doc.setTextColor(110, 110, 110);
        doc.text(`Page ${data.pageNumber}`, pageWidth - marginX, footerY, { align: 'right' });
      },
    });
  }

  const drawnDetailPages = new Set();
  const drawPageChrome = (pageNumber) => {
    if (drawnDetailPages.has(pageNumber)) return;
    drawnDetailPages.add(pageNumber);
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageWidth, 76, 'F');
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Jobs Results', marginX, 32);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(230, 240, 247);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - marginX, 24, { align: 'right' });
    doc.text('VMS PRO - Powered by Pinetechs.com', pageWidth - marginX, 42, { align: 'right' });

    const footerY = doc.internal.pageSize.getHeight() - 24;
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.5);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);
    doc.setTextColor(110, 110, 110);
    doc.text(`Page ${pageNumber}`, pageWidth - marginX, footerY, { align: 'right' });
  };

  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomY = pageHeight - 48;
  if (includeSummary) doc.addPage();
  drawPageChrome(doc.internal.getCurrentPageInfo().pageNumber);
  let cursorY = 96;
  const ensureSpace = (height) => {
    if (cursorY + height <= bottomY) return;
    doc.addPage();
    drawPageChrome(doc.internal.getCurrentPageInfo().pageNumber);
    cursorY = 96;
  };

  ensureSpace(28);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...primary);
  doc.text('Jobs Details', marginX, cursorY);
  cursorY += 16;

  jobs.forEach((job) => {
    const booking = job?.booking || {};
    const client = job?.client || {};
    const containers = job?.containers || [];
    const jobTitle = `Job #${job?.id || '-'} - ${job?.jobStatus || '-'}`;
    const jobMeta = [
      `Booking: ${booking?.number || '-'}`,
      `Client: ${client?.name || '-'}`,
      `Start: ${job?.startDate || '-'}`,
      `Vessel: ${booking?.vesselName || '-'}`,
      `Destination: ${booking?.destination?.name || '-'}`,
      `ERD: ${booking?.earliestReceivingDate || '-'}`,
      `LRD: ${booking?.latestReturnDate || '-'}`,
      `Containers: ${getJobContainersCount(job)}`,
      `Vehicles: ${getJobVehiclesCount(job)}`,
      `Hybrid/Electric: ${getJobHasHybridElectric(job) ? 'Yes' : 'No'}`,
    ];

    ensureSpace(74);
    doc.setFillColor(245, 248, 252);
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.6);
    doc.roundedRect(marginX, cursorY, pageWidth - marginX * 2, 58, 4, 4, 'FD');
    doc.setFontSize(10.5);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...primary);
    doc.text(jobTitle, marginX + 10, cursorY + 16);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(55, 65, 81);
    jobMeta.forEach((line, index) => {
      doc.text(line, marginX + 10 + (index % 5) * 150, cursorY + 32 + Math.floor(index / 5) * 13);
    });
    cursorY += 70;

    if (!containers.length) {
      ensureSpace(18);
      doc.setFontSize(8);
      doc.setTextColor(110, 110, 110);
      doc.text('No containers', marginX + 10, cursorY);
      cursorY += 18;
      return;
    }

    containers.forEach((container, containerIndex) => {
      const shipmentItems = container?.shipmentItems || [];
      const containerTitle = [
        `Container #${containerIndex + 1}`,
        `Number: ${container?.containerNumber || 'N/A'}`,
        `Seal: ${container?.sealNumber || '-'}`,
        `Size: ${container?.containerSize || container?.size || '-'}`,
        `Net Weight: ${container?.netWeight ?? '-'}`,
      ].join('   |   ');

      ensureSpace(58);
      doc.setFillColor(232, 241, 255);
      doc.setDrawColor(179, 218, 255);
      doc.roundedRect(marginX + 10, cursorY, pageWidth - marginX * 2 - 20, 22, 3, 3, 'FD');
      doc.setFontSize(8.5);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...primary);
      doc.text(containerTitle, marginX + 18, cursorY + 14);
      cursorY += 28;

      const vehicleRows = shipmentItems.length
        ? shipmentItems.map((item, index) => {
          const vehicle = item?.vehicle || {};
          return [
            index + 1,
            getJobVehicleTitle(vehicle),
            getJobVehicleVin(vehicle),
            getValueName(vehicle?.fuelType) || '-',
            getValueName(vehicle?.color) || '-',
            vehicle?.weight ?? '-',
          ];
        })
        : [['-', 'No vehicles', '-', '-', '-', '-']];

      doc.autoTable({
        startY: cursorY,
        head: [['#', 'Vehicle', 'VIN', 'Fuel', 'Color', 'Weight']],
        body: vehicleRows,
        theme: 'grid',
        styles: { fontSize: 7.4, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: primary, textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [250, 252, 255] },
        margin: { left: marginX + 10, right: marginX + 10, top: 92, bottom: 40 },
        tableWidth: pageWidth - marginX * 2 - 20,
        columnStyles: {
          0: { halign: 'center', cellWidth: 28 },
          1: { cellWidth: 270 },
          2: { cellWidth: 225 },
          3: { cellWidth: 85 },
          4: { cellWidth: 85 },
          5: { halign: 'center', cellWidth: 73 },
        },
        didDrawPage: (data) => {
          drawPageChrome(doc.internal.getCurrentPageInfo().pageNumber);
        },
      });
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 16;
    });

    ensureSpace(20);
    doc.setDrawColor(180, 196, 212);
    doc.setLineWidth(0.6);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 18;
  });

  doc.setProperties({ title: filename.replace(/\.pdf$/i, '') });
  if (mode === 'preview') {
    try {
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');
    } catch (e) {
      doc.output('dataurlnewwindow');
    }
  } else {
    doc.save(filename);
  }
};

// Optional: Export a single container only
export const exportContainerDetailPDF = (shipment, containerIndex = 0, options = {}, currencySymbol = '$', withConsigneeDock , isSplitShipment ) => {
  if (!shipment) return;
  const containers = shipment.containers || [];
  const c = containers[containerIndex];
  if (!c) return;
  // Use the shipping instructions exporter for a single-container preview/export
  const symbol = (options && options.currencySymbol) ? options.currencySymbol : currencySymbol;
  exportShippingInstructionsPDF({ ...shipment, containers: [c] }, options, symbol, withConsigneeDock, isSplitShipment);
};
exportContainerDetailPDF.prototype = {
  shipment: PropTypes.object.isRequired,
  containerIndex: PropTypes.number,
  options: PropTypes.object,
  currencySymbol: PropTypes.string,
  withConsigneeDock: PropTypes.bool,
  isSplitShipment: PropTypes.bool,
}

// Shipping Instructions PDF - one page per container
export const exportShippingInstructionsPDF = (shipment, options = {}, currencySymbol = '$', withConsigneeDock = false , isSplitShipment = false) => {


  if (!shipment) return;
  const { mode = 'download' } = options; // 'download' | 'preview'
  const booking = shipment.booking || {};
  const containers = Array.isArray(shipment.containers) ? shipment.containers : [];
  const exporter = shipment.exporter || booking.exporter || (containers[0]?.exporter) || {};
  const consignee =   withConsigneeDock ? shipment.consigneeDock || {} : shipment.consignee  || {};
  const notifyParty = shipment.notifyParty || {};
 

  const pickNIF = (o) => o?.nif || o?.NIF || o?.taxId || o?.taxID || o?.taxNumber || o?.vatNumber || o?.tin || o?.TaxNo || null;

  const nif = pickNIF(consignee);
  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const primary = [11, 60, 93];
  const lighterPrimary = [30, 90, 130]; // softer header color for printing
  const light = [248,249,252];

  // Header + footer helpers
  const headerHeight = 64;
  const drawHeader = () => {
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');
    doc.setFontSize(18); doc.setTextColor(255,255,255); doc.setFont(undefined, 'bold');
    doc.text('Shipping Instructions', marginX, 38);
    doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(235,240,245);
    const generated = new Date().toLocaleDateString();
    doc.text(`Date: ${generated}`, pageWidth - marginX, 28, { align: 'right' });
    if (booking.number) doc.text(`Booking: ${booking.number}`, pageWidth - marginX, 44, { align: 'right' });
  };
  const drawFooter = (pageNumber) => {
    const footerY = pageHeight - 36;
    doc.setDrawColor(...primary); doc.setLineWidth(0.5);
    doc.line(marginX, footerY - 8, pageWidth - marginX, footerY - 8);
    doc.setFontSize(9); doc.setTextColor(110,110,110); doc.setFont(undefined,'normal');
    doc.text('VMS PRO • Powered by Pinetechs.com', marginX, footerY);
    doc.text(`Page ${pageNumber}`, pageWidth - marginX, footerY, { align: 'right' });
  };

  // Prepare exporter/consignee wrapped text helpers
  const gapLocal = 12;
  const cardWLocal = (pageWidth - marginX*2 - gapLocal) / 2;
  const lineH = 11;

  const exporterLines = [
    exporter?.name || '-', 
    exporter?.exportNumber ? `Export #: ${exporter.exportNumber}` : '',
    exporter?.street || '',
    exporter?.city || '' ? `${exporter.city} , ${exporter.province || ''} ${exporter.postalCode || ''}`.trim() : '',
    exporter?.country || '',
    `Tel: ${exporter?.phoneNumber || '-'}`,
    `Email: ${exporter?.email || '-'}`
].filter(line => line && line.trim() !== '');  


  const consigneeLines = [ consignee?.name || '-', nif ? `NIF: ${nif}` : '', consignee?.street || '', consignee?.city || '', consignee?.country || '', `Tel: ${consignee?.phoneNumber || '-'}`, `Email: ${consignee?.email || '-'}` ].filter(Boolean);
  const primaryDescription = shipment.primaryDescription || shipment.description || '';
  const descriptionLines = primaryDescription.split('\n').flatMap(l => doc.splitTextToSize(String(l), pageWidth - marginX*2));
  const bookingDestination = booking?.destination?.name || booking?.destination || '-';
  const makePartyLines = (party, includeNif = false) => {
    const partyNif = includeNif ? pickNIF(party) : null;
    return [
      party?.name || '-',
      partyNif ? `NIF: ${partyNif}` : '',
      party?.street || '',
      party?.city || '',
      party?.country || '',
      `Tel: ${party?.phoneNumber || '-'}`,
      `Email: ${party?.email || '-'}`,
    ].filter(Boolean);
  };
 
  const getPartyNumber = (party) => party?.consigneeNumber || party?.notifyPartyNumber || party?.consigneeDockNumber || party?.exportNumber || '-';
  const fmtCurrency = (n) => {
    const num = Number.isFinite(Number(n)) ? Number(n) : 0;
    return `${currencySymbol}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const drawPartyCard = (title, lines, x, y, w, h) => {
    doc.setFillColor(255,255,255);
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, w, h, 4,4,'FD');
    doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text(title, x + 8, y + 18);
    doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30);
    let textY = y + 34;
    lines.forEach(line => { doc.text(line, x + 8, textY); textY += lineH; });
  };
  const drawSplitShipmentItemPage = (c, containerIndex, item, itemIndex, addNewPage) => {
    if (addNewPage) doc.addPage();
    drawHeader();

    const vehicle = item?.vehicle || {};
  
    const itemConsignee = withConsigneeDock ? item?.consigneeDock : item?.consignee;
    const itemNotifyParty = item?.notifyParty;
    const itemDescription = item?.description || primaryDescription;
    const itemDescriptionLines = itemDescription.split('\n').flatMap(l => doc.splitTextToSize(String(l), pageWidth - marginX*2));

    doc.setFillColor(...light); doc.setDrawColor(...primary); doc.setLineWidth(.7);
    doc.roundedRect(marginX - 4, headerHeight + 6, pageWidth - marginX*2 + 8, 26, 4,4,'F');
    doc.setFontSize(14); doc.setTextColor(...primary); doc.setFont(undefined,'bold');
    const containerTitle = `Container ${containerIndex + 1} (${c.containerNumber || 'N/A'}) - Vehicle ${itemIndex + 1}`.toUpperCase();
    doc.text(containerTitle, marginX, headerHeight + 24);

    const cursorY_meta = headerHeight + 36;
    const meta = [
      ['Seal', c.sealNumber || '-', 'Size', c.containerSize || '-'],
      ['Net Weight', vehicle?.weight ?? c.netWeight ?? '-', 'Parts', c.partsInContainer ? 'Yes':'No'],
    ];
    if (exporter) meta.push(['Exporter', exporter.name || '-', 'Export #', exporter.exportNumber || '-']);
    if (itemConsignee) meta.push(['Consignee', itemConsignee.name || '-', 'Consignee #', getPartyNumber(itemConsignee)]);
    doc.autoTable({ startY: cursorY_meta, head: [['Field','Value','Field','Value']], body: meta, theme: 'striped', styles: { fontSize:8, cellPadding:4 }, headStyles: { fillColor: primary, textColor:[255,255,255] }, alternateRowStyles: { fillColor: [250,250,250] }, margin: { left: marginX, right: marginX }, tableWidth: pageWidth - marginX*2, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

    const carrierHead = ['Carrier','Vessel / Voyage','R. Terminal','Equipment'];
    const carrierBody = [[ booking?.shippingLine?.name ||  '-', `${booking?.vesselName || '-'} / ${booking?.voyageNumber || '-'}`, booking?.returnTerminal?.name  || '-', c.containerSize || '-' ]];
    doc.autoTable({ startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerHeight + 40, head: [carrierHead], body: carrierBody, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize: 9, cellPadding: 6 }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

    const infoHead = ['Destination','E.R.D','L.R.D'];
    const infoBody = [[ bookingDestination, booking?.earliestReceivingDate || booking?.erd || '-', booking?.latestReturnDate || booking?.lrd || '-' ]];
    doc.autoTable({ startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerHeight + 12, head: [infoHead], body: infoBody, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize: 9, cellPadding: 6 }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

    const cardY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : headerHeight + 12;
    const localExporterLines = exporterLines.flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 5));
    const localConsigneeLines = makePartyLines(itemConsignee, true).flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 5));
    const cardHLocal = Math.max(64, 24 + Math.max(localExporterLines.length, localConsigneeLines.length) * (lineH + 1));
    drawPartyCard('Exporter', localExporterLines, marginX, cardY, cardWLocal, cardHLocal);
    drawPartyCard(withConsigneeDock ? 'Consignee Dock' : 'Consignee', localConsigneeLines, marginX + cardWLocal + gapLocal, cardY, cardWLocal, cardHLocal);

    let cursorY;
    if (!withConsigneeDock && itemNotifyParty && Object.keys(itemNotifyParty).length) {
      const localNotifyLines = makePartyLines(itemNotifyParty).flatMap(l => doc.splitTextToSize(String(l), pageWidth - marginX*2 - 16));
      const notifyCardH = Math.max(48, 24 + Math.max(1, localNotifyLines.length) * (lineH + 1));
      const notifyY = cardY + cardHLocal + 8;
      drawPartyCard('Notify Party', localNotifyLines, marginX, notifyY, pageWidth - marginX*2, notifyCardH);
      cursorY = notifyY + notifyCardH + 12;
    } else {
      cursorY = cardY + cardHLocal + 12;
    }

    const head = ['#','Year / Make / Model','VIN','Key','Weight (kg)','Value'];
    const body = [[
      '1',
      vehicleDesc(vehicle) || '-',
      vehicle?.vin || '-',
      (vehicle?.key === true) ? 'Yes' : 'No',
      vehicle?.weight != null ? String(Math.round(Number(vehicle.weight))) : '-',
      fmtCurrency(Number(vehicle?.price ?? vehicle?.unitPrice) || 0),
    ]];
    const availableWidth = pageWidth - marginX * 2;
   

   const w0 = 36; // id small
  const w1 = Math.round(availableWidth * 0.45); // description
  const w2 = Math.round(availableWidth * 0.20); // VIN
  const w3 = Math.round(availableWidth * 0.07); // Key (small)
  const w4 = Math.round(availableWidth * 0.08); // Weight
  const wSum = w0 + w1 + w2 + w3 + w4;
  const w5 = availableWidth - wSum; // remaining width for value column (exact fit)
  const colWidths = { 0: w0, 1: w1, 2: w2, 3: w3, 4: w4, 5: w5 };


    doc.autoTable({ startY: cursorY, head: [head], body, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize:9, cellPadding:4 }, columnStyles: { 0:{cellWidth:colWidths[0]}, 1:{cellWidth:colWidths[1]}, 2:{cellWidth:colWidths[2]}, 3:{cellWidth:colWidths[3]}, 4:{cellWidth:colWidths[4], halign:'right'}, 5:{cellWidth:colWidths[5], halign:'right'} }, margin: { left: marginX, right: marginX }, tableWidth: pageWidth - marginX*2, showHead: 'everyPage', didDrawPage: (data) => { drawFooter(data.pageNumber); } });
    cursorY = doc.lastAutoTable.finalY + 12;

    doc.autoTable({ startY: cursorY + 6, head: [['Quantity','Description','Weight (Kg)']], body: [['1', itemDescriptionLines, String(Math.round(Number(vehicle?.weight) || 0))]], theme: 'grid', styles: { fontSize:9, cellPadding:6 }, headStyles: { fillColor: lighterPrimary, textColor:[255,255,255] }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });
  };

  if (containers.length) {
    containers.forEach((c, idx) => {
      const splitItems = Array.isArray(c.shipmentItems) ? c.shipmentItems.filter(item => item?.vehicle) : [];
      if (isSplitShipment && splitItems.length) {
        splitItems.forEach((item, itemIndex) => {
          drawSplitShipmentItemPage(c, idx, item, itemIndex, idx > 0 || itemIndex > 0);
        });
        return;
      }

      if (idx > 0) doc.addPage();
      drawHeader();
      const containerItems = Array.isArray(c.shipmentItems) ? c.shipmentItems.filter(item => item?.vehicle) : [];
      const firstContainerItem = containerItems[0];
      const containerConsignee = withConsigneeDock
        ? firstContainerItem?.consigneeDock || consignee
        : firstContainerItem?.consignee || consignee;
      const containerNotifyParty = firstContainerItem?.notifyParty || notifyParty;
      const containerConsigneeLines = makePartyLines(containerConsignee, true);
      const containerNotifyPartyLines = makePartyLines(containerNotifyParty);

      // Container title
      doc.setFillColor(...light); doc.setDrawColor(...primary); doc.setLineWidth(.7);
      doc.roundedRect(marginX - 4, headerHeight + 6, pageWidth - marginX*2 + 8, 26, 4,4,'F');
      doc.setFontSize(14); doc.setTextColor(...primary); doc.setFont(undefined,'bold');
    const containerTitle = `Container ${idx+1}  (${c.containerNumber || 'N/A'})`.toUpperCase();
    doc.text(containerTitle, marginX, headerHeight + 24);

    // Container meta: place it immediately under the Container title in the page header
    const cursorY_meta = headerHeight + 36;
    const meta = [ ['Seal', c.sealNumber || '-','Size', c.containerSize || '-'], ['Net Weight', c.netWeight ?? '-', 'Parts', c.partsInContainer ? 'Yes':'No'] ];
    if (exporter) meta.push(['Exporter', exporter.name || '-', 'Export #', exporter.exportNumber || '-']);
    if (containerConsignee) meta.push(['Consignee', containerConsignee.name || '-', 'Consignee #', getPartyNumber(containerConsignee)]);
    doc.autoTable({ startY: cursorY_meta, head: [['Field','Value','Field','Value']], body: meta, theme: 'striped', styles: { fontSize:8, cellPadding:4 }, headStyles: { fillColor: primary, textColor:[255,255,255] }, alternateRowStyles: { fillColor: [250,250,250] }, margin: { left: marginX, right: marginX }, tableWidth: pageWidth - marginX*2, didDrawPage: (data) => { drawFooter(data.pageNumber); } });
    // after meta, carrier / vessel / terminal / equipment (flow follows lastAutoTable)
    const carrierHead = ['Carrier','Vessel / Voyage','R. Terminal','Equipment'];
    const carrierBody = [[ booking?.shippingLine?.name ||  '-', `${booking?.vesselName || '-'} / ${booking?.voyageNumber || '-'}`, booking?.returnTerminal?.name  || '-', c.containerSize || '-' ]];
    doc.autoTable({ startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerHeight + 40, head: [carrierHead], body: carrierBody, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize: 9, cellPadding: 6 }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

  // Booking E.R.D / L.R.D
  const infoHead = ['Destination','E.R.D','L.R.D'];
  const infoBody = [[ bookingDestination, booking?.earliestReceivingDate || booking?.erd || '-', booking?.latestReturnDate || booking?.lrd || '-' ]];
  doc.autoTable({ startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerHeight + 12, head: [infoHead], body: infoBody, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize: 9, cellPadding: 6 }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

  // after info, calculate cardY for exporter/consignee below
  const cardY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : headerHeight + 12;

  // Exporter / Consignee cards
  const wrappedExporter = exporterLines.flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 5));
  const wrappedConsignee = containerConsigneeLines.flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 5));
  const contentLines = Math.max(wrappedExporter.length, wrappedConsignee.length);
  const cardHLocal = Math.max(64, 24 + contentLines * (lineH + 1));

  // Exporter card (white fill + border)
  doc.setFillColor(255,255,255);
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.6);
  doc.roundedRect(marginX, cardY, cardWLocal, cardHLocal, 4,4,'FD');
  doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text('Exporter', marginX + 8, cardY + 18);
  doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30);
  let ey = cardY + 34; wrappedExporter.forEach(line => { doc.text(line, marginX + 8, ey); ey += lineH; });

  // Consignee card (explicit white fill + border; ensure dark body text)
  const consX = marginX + cardWLocal + gapLocal;
  doc.setFillColor(255,255,255);
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.6);
  doc.roundedRect(consX, cardY, cardWLocal, cardHLocal, 4,4,'FD');
  doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text('Consignee', consX + 8, cardY + 18);
  doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30);
  let cy = cardY + 34; wrappedConsignee.forEach(line => { doc.text(line, consX + 8, cy); cy += lineH; });

  // If notifyParty exists and we're not using consigneeDock, render a Notify Party card below exporter/consignee
  let cursorY;
  if (!withConsigneeDock && containerNotifyParty && Object.keys(containerNotifyParty).length) {
    const wrappedNotify = containerNotifyPartyLines.flatMap(l => doc.splitTextToSize(String(l), pageWidth - marginX*2 - 16));
    const notifyLines = Math.max(1, wrappedNotify.length);
    const notifyCardH = Math.max(48, 24 + notifyLines * (lineH + 1));
    const notifyY = cardY + cardHLocal + 8;
    doc.setFillColor(255,255,255);
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.6);
    doc.roundedRect(marginX, notifyY, pageWidth - marginX*2, notifyCardH, 4,4,'FD');
    doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text('Notify Party', marginX + 8, notifyY + 18);
    doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30);
    let ny = notifyY + 34; wrappedNotify.forEach(line => { doc.text(line, marginX + 8, ny); ny += lineH; });
    cursorY = notifyY + notifyCardH + 12;
  } else {
    cursorY = cardY + cardHLocal + 12;
  }

      // Vehicles
      const vehicles = containerItems.length ? containerItems.map(item => item.vehicle).filter(Boolean) : c.vehicles || [];
      if (vehicles.length) {
        const head = ['#','Year / Make / Model','VIN','Key','Weight (kg)','Value'];
        const fmtCurrency = (n) => {
          const num = Number.isFinite(Number(n)) ? Number(n) : 0;
          return `${currencySymbol}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };
        const body = vehicles.map((v,i) => ([ String(i+1), vehicleDesc(v) || '-', v.vin || '-', (v?.key === true) ? 'Yes' : 'No', v.weight != null ? String(Math.round(v.weight)) : '-', fmtCurrency(Number(v.price ?? v.unitPrice) || 0) ]));
  // Ensure Vehicles table columns align with the page width. Use proportional widths that fill the available tableWidth.
  const availableWidth = pageWidth - marginX * 2;
  // propose proportional widths for columns: id, desc, vin, key, weight, value
  // compute first N-1 widths and give the remainder to the last column to avoid rounding drift
  
  const w0 = 36; // id small
  const w1 = Math.round(availableWidth * 0.45); // description
  const w2 = Math.round(availableWidth * 0.20); // VIN
  const w3 = Math.round(availableWidth * 0.07); // Key (small)
  const w4 = Math.round(availableWidth * 0.08); // Weight
  const wSum = w0 + w1 + w2 + w3 + w4;
  const w5 = availableWidth - wSum; // remaining width for value column (exact fit)
  const colWidths = { 0: w0, 1: w1, 2: w2, 3: w3, 4: w4, 5: w5 };
  doc.autoTable({ startY: cursorY, head: [head], body, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize:9, cellPadding:4 }, columnStyles: { 0:{cellWidth:colWidths[0]}, 1:{cellWidth:colWidths[1]}, 2:{cellWidth:colWidths[2]}, 3:{cellWidth:colWidths[3], halign:'center'}, 4:{cellWidth:colWidths[4], halign:'right'}, 5:{cellWidth:colWidths[5], halign:'right'} }, margin: { left: marginX, right: marginX }, tableWidth: pageWidth - marginX*2, showHead: 'everyPage', didDrawPage: (data) => { drawFooter(data.pageNumber); } });
        cursorY = doc.lastAutoTable.finalY + 12;
      } else {
        doc.setFontSize(9); doc.setTextColor(120,120,120); doc.text('No vehicles in this container.', marginX, cursorY); cursorY += 18;
      }

      // Per-container summary table: Quantity / Description / Weight
      const qty = vehicles.length;
      const desc = descriptionLines;
      const wsum = Math.round((vehicles || []).reduce((s,v)=> s + (Number(v.weight)||0),0));
      doc.autoTable({ startY: cursorY + 6, head: [['Quantity','Description','Weight (Kg)']], body: [[String(qty), desc, String(wsum)]], theme: 'grid', styles: { fontSize:9, cellPadding:6 }, headStyles: { fillColor: lighterPrimary, textColor:[255,255,255] }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });
    });
  } else {
    // No containers: single page output with booking/exporter/consignee
    drawHeader();
    // Booking E.R.D / L.R.D for no-containers flow
    const infoHead = ['Destination','E.R.D','L.R.D'];
    const infoBody = [[ bookingDestination, booking?.earliestReceivingDate || booking?.erd || '-', booking?.latestReturnDate || booking?.lrd || '-' ]];
    doc.autoTable({ startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : headerHeight + 12, head: [infoHead], body: infoBody, theme: 'grid', headStyles: { fillColor: lighterPrimary, textColor: [255,255,255] }, styles: { fontSize: 9, cellPadding: 6 }, margin: { left: marginX, right: marginX }, didDrawPage: (data) => { drawFooter(data.pageNumber); } });

    // Container meta for no-containers flow (placed under header)
    const metaNo = [ ['Seal', '-','Size', '-'], ['Net Weight', '-', 'Parts', '-' ] ];
    if (exporter) metaNo.push(['Exporter', exporter.name || '-', 'Export #', exporter.exportNumber || '-']);
    if (consignee) metaNo.push(['Consignee', consignee.name || '-', 'Consignee #', consignee.consigneeNumber || '-']);
  // Place the container meta under the header / container-number area (same as container pages)
  const cursorY_meta_no = headerHeight + 36;
  doc.autoTable({ startY: cursorY_meta_no, head: [['Field','Value','Field','Value']], body: metaNo, theme: 'striped', styles: { fontSize:8, cellPadding:4 }, headStyles: { fillColor: primary, textColor:[255,255,255] }, alternateRowStyles: { fillColor: [250,250,250] }, margin: { left: marginX, right: marginX }, tableWidth: pageWidth - marginX*2, didDrawPage: (data) => { drawFooter(data.pageNumber); } });
  const cardYNo = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : headerHeight + 12;

  // Prepare wrapped lines & card height for no-containers flow
  const wrappedExporter = exporterLines.flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 16));
  const wrappedConsignee = consigneeLines.flatMap(l => doc.splitTextToSize(String(l), cardWLocal - 16));
  const contentLinesNo = Math.max(wrappedExporter.length, wrappedConsignee.length);
  const cardHLocal = Math.max(64, 24 + contentLinesNo * (lineH + 1));

  // Exporter / Consignee cards for no-containers flow
  doc.setFillColor(255,255,255);
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.6);
  doc.roundedRect(marginX, cardYNo, cardWLocal, cardHLocal, 4,4,'FD');
  doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text('Exporter', marginX + 8, cardYNo + 18);
  doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30); let eyNo = cardYNo + 34; wrappedExporter.forEach(line => { doc.text(line, marginX + 8, eyNo); eyNo += lineH; });
  const consXNo = marginX + cardWLocal + gapLocal; doc.roundedRect(consXNo, cardYNo, cardWLocal, cardHLocal, 4,4,'FD'); doc.setFontSize(12); doc.setTextColor(...primary); doc.setFont(undefined,'bold'); doc.text('Consignee', consXNo + 8, cardYNo + 18);
  doc.setFontSize(9); doc.setFont(undefined,'normal'); doc.setTextColor(30,30,30); let cyNo = cardYNo + 34; wrappedConsignee.forEach(line => { doc.text(line, consXNo + 8, cyNo); cyNo += lineH; });
    drawFooter(1);
  }

  const filename = options.filename || `shipping_instructions_${shipment.id || booking.number || 'doc'}.pdf`;
  doc.setProperties({
    title: filename.replace(/\.pdf$/i, ''),
  });
  if (mode === 'preview') {
    try { const blobUrl = doc.output('bloburl'); window.open(blobUrl, '_blank'); } catch(e){ doc.output('dataurlnewwindow'); }
  } else {
    doc.save(filename);
  }
};

